import React, { useEffect, useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  FaYoutube,
  FaTrash,
  FaExternalLinkAlt,
  FaWhatsapp,
  FaCopy,
  FaPlus,
  FaPlay,
  FaChevronDown,
  FaChevronUp,
  FaList,
  FaEdit,
  FaTimes,
  FaColumns,
  FaChevronLeft,
  FaChevronRight,
  FaShieldAlt,
  FaInfoCircle
} from 'react-icons/fa';
import { FiPlay, FiList, FiClock, FiVideo, FiMoreVertical, FiEdit3, FiX, FiCheck, FiAirplay, FiMaximize, FiShield, FiLayout, FiMinus } from 'react-icons/fi';
import { toast } from 'react-toastify';
import { useTheme } from '../../../context/ThemeContext';

const VideoSection = ({
  videos,
  videoTitle,
  videoUrl,
  videoUnit,
  isPlaylist,
  onVideoTitleChange,
  onVideoUrlChange,
  onVideoUnitChange,
  onIsPlaylistChange,
  onAddVideo,
  onUpdateVideo,
  onDeleteVideo,
  isAdmin,
  uploadFormLink,
  subjectKey,
  isLoading
}) => {
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [expandedUnits, setExpandedUnits] = useState(['Unit 1']);
  const [isSidebarVisible, setIsSidebarVisible] = useState(true);
  const [editingVideoId, setEditingVideoId] = useState(null);
  const [activeMobileView, setActiveMobileView] = useState('units'); // 'units' or 'video'
  const [player, setPlayer] = useState(null);
  const { isDarkMode } = useTheme();

  const c = {
    bg: 'var(--bg-main)',
    card: 'var(--bg-card)',
    surface: 'var(--bg-surface)',
    border: 'var(--border)',
    text: 'var(--text-main)',
    muted: 'var(--text-muted)',
    accent: 'var(--accent)',
    glass: 'var(--glass)',
    glassBorder: 'var(--glass-border)'
  };

  // Robust YouTube URL Formatter
  const formatYoutubeUrl = (url) => {
    if (!url) return '';
    try {
      let videoId = '';
      if (url.includes('youtube.com/playlist?list=')) {
        const playlistId = new URL(url).searchParams.get('list');
        return `https://www.youtube.com/embed/videoseries?list=${playlistId}&rel=0&modestbranding=0&controls=1&showinfo=1&enablejsapi=1&origin=${window.location.origin}`;
      }
      if (url.includes('youtu.be/')) {
        videoId = url.split('youtu.be/')[1].split(/[?#]/)[0];
      } else if (url.includes('youtube.com/shorts/')) {
        videoId = url.split('youtube.com/shorts/')[1].split(/[?#]/)[0];
      } else if (url.includes('youtube.com/live/')) {
        videoId = url.split('youtube.com/live/')[1].split(/[?#]/)[0];
      } else if (url.includes('youtube.com/watch?v=')) {
        videoId = new URL(url).searchParams.get('v');
      } else if (url.includes('youtube.com/embed/')) {
        videoId = url.split('youtube.com/embed/')[1].split(/[?#]/)[0];
      } else if (url.includes('m.youtube.com/watch?v=')) {
        videoId = new URL(url).searchParams.get('v');
      }
      if (videoId) {
        // Check for playlist parameter in the original URL to keep it in the embed
        const urlObj = new URL(url);
        const listId = urlObj.searchParams.get('list');
        const playlistParam = listId ? `&list=${listId}` : '';

        return `https://www.youtube.com/embed/${videoId}?rel=0&modestbranding=0&iv_load_policy=1&controls=1&showinfo=1&enablejsapi=1${playlistParam}&origin=${window.location.origin}`;
      }
      return url;
    } catch (e) { return url; }
  };

  // Group videos by unit
  const videosByUnit = useMemo(() => {
    const grouped = videos.reduce((acc, video) => {
      const unit = video.unit || 'Unit 1';
      if (!acc[unit]) acc[unit] = [];
      acc[unit].push(video);
      return acc;
    }, {});
    return Object.keys(grouped).sort().reduce((acc, key) => {
      acc[key] = grouped[key];
      return acc;
    }, {});
  }, [videos]);

  useEffect(() => {
    if (videos.length > 0) {
      const searchParams = new URLSearchParams(window.location.search);
      const videoId = searchParams.get('video');
      const unitName = searchParams.get('unit');

      if (videoId) {
        const found = videos.find(v => v.id === videoId);
        if (found) {
          setSelectedVideo(found);
          if (found.unit) setExpandedUnits(prev => Array.from(new Set([...prev, found.unit])));
          setActiveMobileView('video');
          return;
        }
      }

      if (unitName) {
        setExpandedUnits(prev => Array.from(new Set([...prev, unitName])));
        const unitVideos = videosByUnit[unitName];
        if (unitVideos && unitVideos.length > 0 && !selectedVideo) {
          setSelectedVideo(unitVideos[0]);
          return;
        }
      }

      if (!selectedVideo) {
        const firstUnit = Object.keys(videosByUnit)[0];
        if (firstUnit && videosByUnit[firstUnit].length > 0) {
          setSelectedVideo(videosByUnit[firstUnit][0]);
        }
      }
    }
  }, [videosByUnit, videos]); // removed selectedVideo from deps to avoid infinite loops

  // YouTube IFrame API Initialization
  useEffect(() => {
    // Load YouTube IFrame API if not already loaded
    if (!window.YT) {
      const tag = document.createElement('script');
      tag.src = "https://www.youtube.com/iframe_api";
      const firstScriptTag = document.getElementsByTagName('script')[0];
      firstScriptTag.parentNode.insertBefore(tag, firstScriptTag);
    }

    // Callback when API is ready
    window.onYouTubeIframeAPIReady = () => {
      console.log('YouTube API Ready');
    };
  }, []);

  // Initialize/Update Player when selectedVideo changes
  useEffect(() => {
    if (selectedVideo && window.YT && window.YT.Player) {
      // Small delay to ensure iframe is rendered
      const timer = setTimeout(() => {
        new window.YT.Player(`youtube-player-${selectedVideo.id}`, {
          events: {
            onReady: (event) => {
              setPlayer(event.target);
            },
            onStateChange: (event) => {
              // Handle video changes within the native playlist
              if (event.data === window.YT.PlayerState.PLAYING) {
                const updatedVideoData = event.target.getVideoData();
                if (updatedVideoData && updatedVideoData.video_id) {
                  // We can't easily update selectedVideo.link here without side effects,
                  // but we ensure the player remains the source of truth for native navigation.
                  console.log('Now playing:', updatedVideoData.title);
                }
              }
            }
          }
        });
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [selectedVideo]);


  const toggleUnit = (unit) => {
    setExpandedUnits(prev =>
      prev.includes(unit) ? prev.filter(u => u !== unit) : [...prev, unit]
    );
  };

  const handleEditClick = (video) => {
    setEditingVideoId(video.id);
    onVideoTitleChange({ target: { value: video.title } });
    onVideoUrlChange({ target: { value: video.link } });
    onVideoUnitChange({ target: { value: video.unit || 'Unit 1' } });
    onIsPlaylistChange({ target: { checked: !!video.isPlaylist } });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const cancelEdit = () => {
    setEditingVideoId(null);
    onVideoTitleChange({ target: { value: '' } });
    onVideoUrlChange({ target: { value: '' } });
    onVideoUnitChange({ target: { value: 'Unit 1' } });
    onIsPlaylistChange({ target: { checked: false } });
  };

  const handleAction = () => {
    if (!videoTitle.trim() || !videoUrl.trim()) {
      toast.error('Title and URL are required');
      return;
    }
    const formattedLink = formatYoutubeUrl(videoUrl);
    if (editingVideoId) {
      onUpdateVideo(editingVideoId, {
        title: videoTitle,
        link: formattedLink,
        unit: videoUnit,
        isPlaylist: isPlaylist
      });
      setEditingVideoId(null);
    } else {
      onAddVideo();
    }
  };

  const handleCopyLink = (type = 'video', data = null) => {
    const target = data || selectedVideo;
    if (!target) return;

    let link = `${window.location.origin}${window.location.pathname}?tab=Videos`;
    if (type === 'video') link += `&video=${target.id}`;
    else if (type === 'unit') link += `&unit=${encodeURIComponent(target)}`;

    navigator.clipboard.writeText(link);
    toast.success(`${type === 'video' ? 'Video' : 'Unit'} link copied!`);
  };

  const handleShareWhatsApp = (type = 'video', data = null) => {
    const target = data || selectedVideo;
    if (!target) return;

    let link = `${window.location.origin}${window.location.pathname}?tab=Videos`;
    if (type === 'video') {
      link += `&video=${target.id}`;
      const message = `Check out this lesson: ${target.title}\n\n${link}`;
      window.open(`https://wa.me/?text=${encodeURIComponent(message)}`, "_blank");
    } else {
      link += `&unit=${encodeURIComponent(target)}`;
      const message = `Check out this course unit: ${target}\n\n${link}`;
      window.open(`https://wa.me/?text=${encodeURIComponent(message)}`, "_blank");
    }
  };

  const handleConnectDiscuss = () => {
    if (!selectedVideo) return;
    const link = `${window.location.origin}${window.location.pathname}?tab=Videos&video=${selectedVideo.id}`;
    const message = `Hi! I'm studying "${selectedVideo.title}" in ${subjectKey}. Let's discuss this!\n\n${link}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(message)}`, "_blank");
  };

  return (
    <div className="w-full max-w-[1600px] mx-auto px-0 md:px-0">
      {isAdmin && (
        <motion.div
          className="mb-12 p-10 rounded-[2.5rem] border relative overflow-hidden transition-all duration-300 shadow-2xl"
          style={{ backgroundColor: c.card, borderColor: editingVideoId ? 'rgba(34, 197, 94, 0.4)' : c.glassBorder }}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <div className="flex items-center justify-between mb-8">
            <div className="flex items-center gap-4">
              <div className={`w-12 h-12 rounded-2xl flex items-center justify-center text-lg border ${editingVideoId ? 'bg-green-500/20 text-green-400 border-green-500/30' : 'bg-red-500/20 text-red-400 border-red-500/30'}`}>
                {editingVideoId ? <FiEdit3 /> : <FaPlus />}
              </div>
              <h3 className="text-2xl font-black tracking-tight">{editingVideoId ? 'Update Lesson' : 'Add New Content'}</h3>
            </div>
            {editingVideoId && (
              <button onClick={cancelEdit} className="p-3 rounded-xl bg-white/5 hover:bg-white/10 text-gray-400 transition-all">
                <FiX size={20} />
              </button>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            <div className="space-y-3">
              <label className="text-[11px] font-bold uppercase tracking-[0.2em] pl-1 opacity-50" style={{ color: c.text }}>Title</label>
              <input
                type="text"
                value={videoTitle}
                onChange={onVideoTitleChange}
                placeholder="Lesson Title"
                className="w-full p-4 rounded-2xl border focus:outline-none focus:ring-2 focus:ring-red-500/20 transition-all text-sm font-semibold"
                style={{ backgroundColor: c.surface, borderColor: c.glassBorder, color: c.text }}
              />
            </div>
            <div className="space-y-3">
              <label className="text-[11px] font-bold uppercase tracking-[0.2em] pl-1 opacity-50" style={{ color: c.text }}>YouTube Link</label>
              <input
                type="text"
                value={videoUrl}
                onChange={onVideoUrlChange}
                placeholder="https://youtube.com/..."
                className="w-full p-4 rounded-2xl border focus:outline-none focus:ring-2 focus:ring-red-500/20 transition-all text-sm font-semibold"
                style={{ backgroundColor: c.surface, borderColor: c.glassBorder, color: c.text }}
              />
            </div>
            <div className="space-y-3">
              <label className="text-[11px] font-bold uppercase tracking-[0.2em] pl-1 opacity-50" style={{ color: c.text }}>Target Unit</label>
              <select
                value={videoUnit}
                onChange={onVideoUnitChange}
                className="w-full p-4 rounded-2xl border focus:outline-none focus:ring-2 focus:ring-red-500/20 transition-all text-sm font-bold"
                style={{ backgroundColor: c.surface, borderColor: c.glassBorder, color: c.text }}
              >
                {['Unit 1', 'Unit 2', 'Unit 3', 'Unit 4', 'Experiments', 'Revision'].map(u => (
                  <option key={u} value={u} className="bg-neutral-900">{u}</option>
                ))}
              </select>
            </div>
            <div className="flex flex-col justify-end">
              <div className="flex items-center gap-4 p-4 rounded-2xl border bg-opacity-30 cursor-pointer group" style={{ backgroundColor: c.surface, borderColor: c.glassBorder }} onClick={() => onIsPlaylistChange({ target: { checked: !isPlaylist } })}>
                <div className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-all ${isPlaylist ? 'bg-red-500 border-red-500' : 'border-gray-600'}`}>
                  {isPlaylist && <FiCheck className="text-white text-xs" />}
                </div>
                <label className="text-[11px] font-black uppercase tracking-widest cursor-pointer select-none" style={{ color: isPlaylist ? c.accent : c.muted }}>
                  Playlist Mode
                </label>
              </div>
            </div>
          </div>

          <div className="flex gap-4">
            <motion.button
              onClick={handleAction}
              disabled={isLoading}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className={`flex-1 py-5 rounded-[1.2rem] text-white font-black text-sm shadow-2xl flex items-center justify-center gap-3 disabled:opacity-50 ${editingVideoId ? 'bg-gradient-to-r from-green-600 to-green-500 shadow-green-900/30' : 'bg-gradient-to-r from-red-600 to-red-500 shadow-red-900/30'}`}
            >
              {isLoading ? <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : (editingVideoId ? <><FiCheck size={18} /> SAVE UPDATES</> : <><FaYoutube size={18} /> PUBLISH LECTURE</>)}
            </motion.button>
          </div>
        </motion.div>
      )}

      {videos.length === 0 ? (
        <div className="text-center py-32 rounded-[4rem] border bg-gradient-to-b from-red-500/5 to-transparent relative overflow-hidden" style={{ borderColor: 'var(--border)' }}>
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-64 h-64 bg-red-500/10 blur-[100px] rounded-full"></div>
          <FiVideo className="mx-auto text-7xl mb-8" style={{ color: 'var(--text-muted)' }} />
          <h3 className="text-3xl font-black mb-4" style={{ color: 'var(--text-main)' }}>Classroom Under Setup</h3>
          <p className="max-w-md mx-auto font-medium text-lg mb-10" style={{ color: 'var(--text-muted)' }}>We're digitizing the syllabus for this subject. Expect premium lectures soon.</p>
          <a href={uploadFormLink} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-3 px-10 py-4 border rounded-2xl text-xs font-black uppercase tracking-widest transition-all" style={{ backgroundColor: 'var(--bg-surface)', borderColor: 'var(--border)', color: 'var(--text-main)' }}>
            <FaExternalLinkAlt size={14} /> Request Material
          </a>
        </div>
      ) : (
        <div className="flex flex-col gap-6 md:gap-10">
          {/* Mobile View Switcher Logic */}
          <div className="block lg:hidden w-full px-4">
            {activeMobileView === 'video' && (
              <button
                onClick={() => setActiveMobileView('units')}
                className="mb-4 flex items-center gap-2 px-4 py-2 bg-red-500/10 border border-red-500/20 rounded-xl text-[10px] font-black uppercase tracking-widest text-red-500 shadow-sm"
              >
                <FaChevronLeft size={10} /> Back to Module Library
              </button>
            )}
          </div>
          {/* Header Controls */}
          <div className="flex justify-between items-center px-6">
            <div className="flex items-center gap-3 md:gap-4">
              <button
                onClick={() => setIsSidebarVisible(!isSidebarVisible)}
                className={`hidden lg:flex p-3 md:p-4 rounded-xl md:rounded-2xl border transition-all duration-300 group ${isSidebarVisible ? '' : 'bg-red-500 text-white border-red-500 shadow-xl shadow-red-900/40'}`}
                style={{ backgroundColor: isSidebarVisible ? 'var(--bg-surface)' : '', borderColor: isSidebarVisible ? 'var(--border)' : '', color: isSidebarVisible ? 'var(--text-muted)' : '' }}
                title={isSidebarVisible ? "Hide Course Library" : "Show Course Library"}
              >
                <FiLayout size={18} className={`transition-transform duration-500 ${isSidebarVisible ? '' : 'rotate-180'}`} />
              </button>
              <div className="flex flex-col">
                <span className="text-[9px] font-black uppercase tracking-[0.3em]" style={{ color: 'var(--text-muted)' }}>Learning Path</span>
                <h4 className="font-bold text-xs md:text-sm tracking-tight" style={{ color: 'var(--text-main)' }}>{isSidebarVisible ? 'Multi-Module Library' : 'Focus View'}</h4>
              </div>
            </div>
            {selectedVideo && (
              <div className="hidden lg:flex items-center gap-4 px-6 py-2.5 rounded-full border backdrop-blur-md" style={{ backgroundColor: 'var(--bg-surface)', borderColor: 'var(--border)' }}>
                <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse"></div>
                <span className="text-[10px] font-black uppercase tracking-[0.15em]" style={{ color: 'var(--text-muted)' }}>CurrentSession: <span style={{ color: 'var(--text-main)' }} className="ml-1">{selectedVideo.title}</span></span>
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-10 items-start">
            {/* Main Content: Cinematic Player & Unit Library */}
            <div className={`${isSidebarVisible ? 'lg:col-span-8' : 'lg:col-span-12'} space-y-4 md:space-y-8 transition-all duration-700 ease-in-out`}>
              {/* Player Section - Desktop View */}
              <div className="hidden lg:block">
                <div className="aspect-video relative rounded-none md:rounded-[3rem] overflow-hidden shadow-[0_32px_100px_-20px_rgba(0,0,0,0.8)] border-b md:border group" style={{ backgroundColor: '#000', borderColor: 'var(--border)' }}>
                  {selectedVideo ? (
                    <div className="relative w-full h-full">
                      <iframe
                        id={`youtube-player-${selectedVideo.id}`}
                        src={formatYoutubeUrl(selectedVideo.link)}
                        title={selectedVideo.title}
                        className="w-full h-full"
                        frameBorder="0"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                      />
                    </div>
                  ) : (
                    <div className="w-full h-full flex flex-col items-center justify-center text-center p-20" style={{ backgroundColor: 'var(--bg-card)' }}>
                      <FiPlay size={40} className="text-gray-700 animate-pulse ml-1" />
                      <p className="font-bold uppercase tracking-[0.2em] text-xs mt-4" style={{ color: 'var(--text-muted)' }}>Awaiting session selection</p>
                    </div>
                  )}
                </div>

                {selectedVideo && (
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="p-5 md:p-8 rounded-2xl md:rounded-[2.5rem] border relative overflow-hidden transition-all duration-500 backdrop-blur-2xl mx-4 md:mx-0 shadow-xl mt-6"
                    style={{ borderColor: 'var(--border)', backgroundColor: 'var(--bg-card)' }}
                  >
                    <div className="absolute -top-24 -right-24 w-64 h-64 bg-red-500/5 blur-[100px] rounded-full pointer-events-none"></div>

                    <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6 relative z-10">
                      <div className="flex-1 space-y-3">
                        <div className="flex items-center gap-2">
                          <span className="px-3 py-1 bg-red-500/10 border border-red-500/20 text-red-400 text-[8px] font-black uppercase tracking-[0.2em] rounded-full shadow-inner">COURSE MODULE</span>
                          <div className="flex items-center gap-1.5 px-2.5 py-1 border rounded-full text-[8px] font-bold uppercase tracking-widest" style={{ backgroundColor: 'var(--bg-surface)', borderColor: 'var(--border)', color: 'var(--text-muted)' }}>
                            <FiList size={8} /> {selectedVideo.unit || 'Unit 1'}
                          </div>
                        </div>
                        <h2 className="text-lg md:text-2xl font-black tracking-tight leading-tight uppercase" style={{ color: 'var(--text-main)' }}>{selectedVideo.title}</h2>
                        <div className="flex items-center gap-4 pt-1">
                          <div className="flex items-center gap-1.5 text-[9px] font-bold text-gray-500 uppercase"><FiClock className="text-red-500" /><span>Standard Duration</span></div>
                        </div>
                      </div>

                      <button
                        onClick={handleConnectDiscuss}
                        className="flex-shrink-0 w-12 h-12 md:w-14 md:h-14 flex items-center justify-center bg-[#25D366] hover:bg-[#22c35e] text-white rounded-full md:rounded-2xl shadow-lg transition-all hover:-translate-y-1 active:translate-y-0"
                        title="Connect & Discuss on WhatsApp"
                      >
                        <FaWhatsapp size={24} />
                      </button>
                    </div>
                  </motion.div>
                )}
              </div>

              {/* Mobile Full-Screen Video Modal */}
              <AnimatePresence>
                {activeMobileView === 'video' && selectedVideo && (
                  <motion.div
                    initial={{ y: "100%" }}
                    animate={{ y: 0 }}
                    exit={{ y: "100%" }}
                    transition={{ type: "spring", damping: 25, stiffness: 200 }}
                    className="fixed inset-0 z-[1000] lg:hidden flex flex-col"
                    style={{ backgroundColor: 'var(--bg-main)' }}
                  >
                    {/* Header */}
                    <div className="flex items-center justify-between p-4 border-b backdrop-blur-xl sticky top-0 z-20" style={{ backgroundColor: 'var(--bg-main)', borderColor: 'var(--border)' }}>
                      <button
                        onClick={() => setActiveMobileView('units')}
                        className="p-2 rounded-xl flex items-center gap-2 hover:bg-white/5 transition-all text-red-500"
                      >
                        <FaChevronLeft size={14} />
                        <span className="text-[10px] font-black uppercase tracking-widest">Back to Modules</span>
                      </button>
                      <div className="flex items-center gap-3">
                        <span className="text-[10px] font-black tracking-tighter opacity-50 uppercase" style={{ color: 'var(--text-muted)' }}>Now Playing</span>
                        <div className="w-2 h-2 rounded-full bg-red-600 animate-pulse"></div>
                      </div>
                    </div>

                    {/* Content Scroll Area */}
                    <div className="flex-1 overflow-y-auto pb-10">
                      {/* Fixed Player on Mobile Modal */}
                      <div className="aspect-video relative sticky top-0 z-10 bg-black shadow-2xl">
                        <iframe
                          id={`youtube-player-mobile-${selectedVideo.id}`}
                          src={formatYoutubeUrl(selectedVideo.link)}
                          title={selectedVideo.title}
                          className="w-full h-full"
                          frameBorder="0"
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                          allowFullScreen
                        />
                      </div>

                      {/* Video Detail Card (Compact for Mobile) */}
                      <div className="p-6">
                        <div className="p-5 rounded-2xl border mb-8" style={{ backgroundColor: 'var(--bg-card)', borderColor: 'var(--border)' }}>
                          <div className="flex items-center gap-2 mb-3">
                            <span className="px-3 py-1 bg-red-500/10 border border-red-500/20 text-red-400 text-[8px] font-black uppercase tracking-widest rounded-full">Unit {selectedVideo.unit || '1'}</span>
                            <span className="px-3 py-1 bg-green-500/10 border border-green-500/20 text-green-400 text-[8px] font-black uppercase tracking-widest rounded-full">Verified Content</span>
                          </div>
                          <h2 className="text-xl font-black tracking-tight leading-tight uppercase mb-8" style={{ color: 'var(--text-main)' }}>{selectedVideo.title}</h2>

                          <button
                            onClick={handleConnectDiscuss}
                            className="w-full flex items-center justify-center gap-3 py-4 bg-[#25D366] text-white font-black rounded-xl shadow-lg shadow-green-900/20 text-xs uppercase tracking-widest active:scale-95 transition-all"
                          >
                            <FaWhatsapp size={18} />
                            Join Discussion
                          </button>
                        </div>

                        {/* Integrated Playlist (Unit Library) */}
                        <div className="space-y-4">
                          <div className="flex items-center gap-3 mb-6">
                            <div className="w-8 h-8 rounded-lg bg-red-500/10 flex items-center justify-center text-red-500 border border-red-500/20">
                              <FiList size={16} />
                            </div>
                            <h4 className="font-black text-sm uppercase tracking-widest" style={{ color: 'var(--text-main)' }}>Playlist Modules</h4>
                          </div>

                          {Object.entries(videosByUnit).map(([unit, unitVideos]) => (
                            <div key={unit} className="border rounded-xl overflow-hidden mb-3" style={{ backgroundColor: 'var(--bg-surface)', borderColor: 'var(--border)' }}>
                              <div className="flex items-center justify-between p-4 cursor-pointer" onClick={() => toggleUnit(unit)}>
                                <span className="text-[10px] font-black uppercase tracking-widest" style={{ color: 'var(--text-main)' }}>{unit}</span>
                                <FaChevronDown size={10} className={`transition-transform duration-300 ${expandedUnits.includes(unit) ? 'rotate-180' : ''}`} style={{ color: 'var(--text-muted)' }} />
                              </div>
                              <AnimatePresence>
                                {expandedUnits.includes(unit) && (
                                  <motion.div initial={{ height: 0 }} animate={{ height: 'auto' }} exit={{ height: 0 }} className="px-2 pb-2">
                                    {unitVideos.map(video => (
                                      <button
                                        key={video.id}
                                        onClick={() => { setSelectedVideo(video); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                                        className={`w-full text-left p-3 rounded-xl mb-1 flex items-start gap-4 transition-all ${selectedVideo?.id === video.id ? 'bg-red-500/10 border border-red-500/20' : 'hover:bg-white/5'}`}
                                      >
                                        <div className={`mt-1 flex-shrink-0 ${selectedVideo?.id === video.id ? 'text-red-500' : 'text-gray-700'}`}>
                                          <FaPlay size={8} />
                                        </div>
                                        <h5 className={`text-[11px] font-bold leading-snsug line-clamp-2 ${selectedVideo?.id === video.id ? 'text-red-500' : ''}`} style={{ color: selectedVideo?.id === video.id ? '#ef4444' : 'var(--text-main)' }}>{video.title}</h5>
                                      </button>
                                    ))}
                                  </motion.div>
                                )}
                              </AnimatePresence>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Mobile Main Unit Selector (Hidden when modal is open) */}
              <div className={`${activeMobileView === 'video' ? 'hidden lg:block' : 'block'}`}>
                <div className="block lg:hidden mx-6 pt-8 pb-4 border-t mt-8" style={{ borderColor: 'var(--border)' }}>
                  <div className="flex items-center gap-4 mb-4">
                    <div className="w-10 h-10 rounded-xl bg-red-500/10 flex items-center justify-center text-red-500 border border-red-500/20 shadow-inner">
                      <FiList size={18} />
                    </div>
                    <div className="flex flex-col">
                      <span className="text-[9px] font-black uppercase tracking-[0.3em]" style={{ color: 'var(--text-muted)' }}>Playlist</span>
                      <h4 className="font-bold text-lg leading-tight" style={{ color: 'var(--text-main)' }}>Course Modules</h4>
                    </div>
                  </div>
                </div>

                <div className="block lg:hidden space-y-4 px-4 pb-20">
                  {Object.entries(videosByUnit).map(([unit, unitVideos]) => (
                    <div key={unit} className="border rounded-2xl overflow-hidden transition-all duration-300" style={{ backgroundColor: 'var(--bg-surface)', borderColor: 'var(--border)' }}>
                      <div className="flex items-center justify-between p-4 cursor-pointer" onClick={() => toggleUnit(unit)}>
                        <div className="flex items-center gap-3">
                          <div className={`w-8 h-8 rounded-lg flex items-center justify-center text-[10px] font-black transition-all ${expandedUnits.includes(unit) ? 'bg-red-500 text-white shadow-lg' : ''}`} style={{ backgroundColor: expandedUnits.includes(unit) ? '#ef4444' : 'var(--bg-card)', color: expandedUnits.includes(unit) ? '#fff' : 'var(--text-muted)' }}>
                            {unit.split(' ')[1] || '0' + (Object.keys(videosByUnit).indexOf(unit) + 1)}
                          </div>
                          <span className="text-xs font-black uppercase tracking-widest" style={{ color: 'var(--text-main)' }}>{unit}</span>
                        </div>
                        <FaChevronDown size={10} className={`transition-transform duration-300 ${expandedUnits.includes(unit) ? 'rotate-180' : ''}`} style={{ color: 'var(--text-muted)' }} />
                      </div>
                      <AnimatePresence>
                        {expandedUnits.includes(unit) && (
                          <motion.div initial={{ height: 0 }} animate={{ height: 'auto' }} exit={{ height: 0 }} className="px-2 pb-2">
                            {unitVideos.map(video => (
                              <button
                                key={video.id}
                                onClick={() => { setSelectedVideo(video); setActiveMobileView('video'); }}
                                className={`w-full text-left p-4 rounded-xl mb-1 flex items-start gap-4 transition-all ${selectedVideo?.id === video.id ? 'bg-red-500/10 border border-red-500/20' : 'hover:bg-white/5'}`}
                              >
                                <div className={`mt-1 flex-shrink-0 ${selectedVideo?.id === video.id ? 'text-red-500' : 'text-gray-700'}`}>
                                  {video.isPlaylist ? <FiList size={12} /> : <FaPlay size={8} />}
                                </div>
                                <h5 className={`text-xs font-bold leading-snsug line-clamp-2 ${selectedVideo?.id === video.id ? 'text-red-500' : ''}`} style={{ color: selectedVideo?.id === video.id ? '#ef4444' : 'var(--text-main)' }}>{video.title}</h5>
                              </button>
                            ))}
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Sidebar: Premium Library Navigation (Desktop Only Grid Location) */}
            <AnimatePresence>
              {isSidebarVisible && (
                <motion.div
                  initial={{ x: 100, opacity: 0 }}
                  animate={{ x: 0, opacity: 1 }}
                  exit={{ x: 100, opacity: 0 }}
                  transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
                  className="hidden lg:block lg:col-span-4 h-full"
                >
                  <div className="sticky top-12 p-8 rounded-[3rem] border backdrop-blur-xl" style={{ borderColor: 'var(--border)', backgroundColor: 'var(--bg-card)' }}>
                    <div className="flex items-center justify-between mb-10">
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 rounded-xl flex items-center justify-center border" style={{ backgroundColor: 'var(--bg-surface)', borderColor: 'var(--border)', color: 'var(--accent)' }}>
                          <FiList size={20} />
                        </div>
                        <div className="flex flex-col">
                          <span className="text-[9px] font-black uppercase tracking-[0.3em]" style={{ color: 'var(--text-muted)' }}>Curriculum</span>
                          <h4 className="font-bold text-lg leading-tight" style={{ color: 'var(--text-main)' }}>Lessons</h4>
                        </div>
                      </div>
                      <button onClick={() => setIsSidebarVisible(false)} className="w-8 h-8 rounded-lg flex items-center justify-center transition-all" style={{ color: 'var(--text-muted)' }}>
                        <FaChevronRight size={14} />
                      </button>
                    </div>

                    <div className="space-y-4 max-h-[70vh] overflow-y-auto no-scrollbar pr-2">
                      {Object.entries(videosByUnit).map(([unit, unitVideos]) => (
                        <div key={unit} className="border rounded-[1.8rem] overflow-hidden transition-all duration-500" style={{ backgroundColor: expandedUnits.includes(unit) ? 'var(--bg-surface)' : 'var(--bg-card)', borderColor: 'var(--border)' }}>
                          <div className="flex items-center justify-between p-5 cursor-pointer" onClick={() => toggleUnit(unit)}>
                            <div className="flex items-center gap-4 flex-1">
                              <div className={`w-9 h-9 rounded-xl flex items-center justify-center text-xs font-black transition-all ${expandedUnits.includes(unit) ? 'bg-red-500 text-white' : ''}`} style={{ backgroundColor: expandedUnits.includes(unit) ? '' : 'var(--bg-surface)', color: expandedUnits.includes(unit) ? '' : 'var(--text-muted)' }}>
                                {unit.split(' ')[1] || '0' + (Object.keys(videosByUnit).indexOf(unit) + 1)}
                              </div>
                              <div className="text-left">
                                <p className="text-[11px] font-black uppercase tracking-widest leading-none mb-1.5" style={{ color: expandedUnits.includes(unit) ? 'var(--text-main)' : 'var(--text-muted)' }}>{unit}</p>
                                <p className="text-[9px] font-bold uppercase tracking-tighter" style={{ color: 'var(--text-muted)', opacity: 0.6 }}>{unitVideos.length} SESSIONS</p>
                              </div>
                            </div>
                            <FaChevronDown size={10} className={`transition-transform duration-500 ${expandedUnits.includes(unit) ? 'rotate-180' : ''}`} style={{ color: 'var(--text-muted)' }} />
                          </div>

                          <AnimatePresence>
                            {expandedUnits.includes(unit) && (
                              <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="px-3 pb-3">
                                <div className="space-y-2">
                                  {unitVideos.map((video) => (
                                    <div key={video.id} className="relative group/item">
                                      <button onClick={() => setSelectedVideo(video)} className={`w-full text-left p-4 rounded-2xl transition-all flex items-start gap-4 relative overflow-hidden ${selectedVideo?.id === video.id ? 'bg-red-500/10 border border-red-500/20' : 'hover:bg-white/5'}`}>
                                        <div className={`mt-1.5 flex-shrink-0 ${selectedVideo?.id === video.id ? 'text-red-500' : 'text-gray-700'}`}>
                                          {video.isPlaylist ? <FiList size={14} /> : (selectedVideo?.id === video.id ? <FiAirplay size={14} /> : <FaPlay size={10} />)}
                                        </div>
                                        <div className="flex-1 min-w-0" onClick={() => setActiveMobileView('video')}>
                                          <h5 className={`text-[13px] font-bold leading-snsug line-clamp-2 ${selectedVideo?.id === video.id ? 'text-white' : ''}`} style={{ color: selectedVideo?.id === video.id ? '#fff' : 'var(--text-main)' }}>{video.title}</h5>
                                          <span className={`text-[9px] font-black uppercase tracking-widest mt-2 block ${selectedVideo?.id === video.id ? 'text-red-400' : 'text-gray-600'}`}>{video.isPlaylist ? 'COURSE' : 'LECTURE'}</span>
                                        </div>
                                      </button>
                                      {isAdmin && (
                                        <div className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center gap-1 opacity-0 group-hover/item:opacity-100 transition-all z-20">
                                          <button onClick={(e) => { e.stopPropagation(); handleEditClick(video); }} className="p-2.5 bg-neutral-900 border border-white/10 text-gray-500 hover:text-green-400 rounded-xl" title="Edit"><FaEdit size={12} /></button>
                                          <button onClick={(e) => { e.stopPropagation(); onDeleteVideo(video.id); }} className="p-2.5 bg-neutral-900 border border-white/10 text-gray-500 hover:text-red-400 rounded-xl" title="Delete"><FaTrash size={12} /></button>
                                        </div>
                                      )}
                                    </div>
                                  ))}
                                </div>
                              </motion.div>
                            )}
                          </AnimatePresence>
                        </div>
                      ))}
                    </div>

                    <div className="mt-8 p-6 rounded-2xl border border-green-500/20 bg-green-500/5 relative overflow-hidden group/info">
                      <FaInfoCircle className="absolute -bottom-2 -right-2 text-4xl text-green-500/10" />
                      <p className="text-[10px] font-bold text-green-400 leading-relaxed uppercase tracking-widest">{subjectKey} Library verified by SnapED codeCampus Council.</p>
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      )}
    </div>
  );
};

export default VideoSection;